import angular from 'angular';
import csmTools from '../../util/tools/tools';

import './importForm.scss';
import importFormTemplate from './importForm.template.html';
import { importProgressBar } from './importProgressBar';
import { inputFileButton } from './inputFileButton';
import { resultRow } from './resultRow';
import { uploadBar } from './uploadBar';

/**
 * @ngdoc  overview
 * @module csm.ui.importForm
 * @description
 * Form for posting a CSV import file to the server. The server side object
 * `csm.web.pages.import.importFormController` receives the file, imports the
 * data, and updates the page by writing, to a hidden IFRAME, commands to the
 * `window.scopeInterface` that manipulate controls in the window parent page.
 *
 * Note: `scopeInterface` calls from the IFRAME are outside of the application
 *       watch cycles and will not trigger a digest cycle. `$timeout` is used to
 *       fire a digest cycle to update parent page views with scope changes.
 *
 * This is an adaptation of the original tool that handled posting a file to an
 * .aspx page and this function is still supported as the default. In this case,
 * the FORM element is POSTed to `handler` and the form `target` is set to the
 * embedded IFRAME and server commands in response update parent page.
 *
 * This version includes the `using-ajax` option which provides for uploading to
 * `handler` using `ngFileUpload` and a progress bar is added to the page to show
 * the upload progress. `handler` saves the upload to a temporary file and
 * responds with the file name. Then the IFRAME `src` property is set to `handler`
 * with the query variable `tempFileName` sending response commands to IFRAME.
 *******************************************************************************/
export default angular
  .module('csm.ui.importForm', [csmTools])

  /*******************************************************************************
   * Constant: CONST */
  /**
   * @ngdoc     object
   * @name      CONST
   * @module    csm.ui.importForm
   * @description
   * Constants added to `importForm` scope.
   *******************************************************************************/
  .constant('CONST', {
    /**
     * @property {string} UploadBarID
     * `id` property of upload file progress bar.
     */
    UploadBarID: 'barUploadFile',

    /**
     * @property {string} UploadFieldName
     * FORM `<input type="FILE">` field name. Must be the same as the field name
     * on the server.
     *
     * TODO: This field name is not necessary and could always be set to 'file'
     * TODO: which is the default for `ngFileUpload`. This would be a breaking
     * TODO: change until the server pages are updated. Also, it should be set on
     * TODO: the server and passed to the `importForm` component.
     */
    UploadFieldName: 'UPLOADFILE'
  })
  .directive('importForm', importFormDirective)
  .directive('importProgressBar', importProgressBar)
  .component('inputFileButton', inputFileButton)
  .directive('resultRow', resultRow)
  .directive('uploadBar', uploadBar).name;

importFormDirective.$inject = [];

/*******************************************************************************
 * importFormDirective */
/**
 * Form for posting a CSV import file to the server. The server side object
 * `csm.web.pages.import.importFormController` receives the file, imports the
 * data, and updates the page by writing, to a hidden IFRAME, commands to the
 * `scopeInterface` that manipulate controls in the window parent page.
 *
 *   <import-form>
 *     <upload-bar id="barValidate" text="Validating Data"></upload-bar>
 *     <upload-bar id="barProcess" text="Processing Data"></upload-bar>
 *     <result-row text="Items Imported: "></result-row>
 *   </import-form>
 *******************************************************************************/
/* @ngInject */ function importFormDirective() {
  return {
    restrict: 'E',
    template: importFormTemplate,
    controller: importFormController,
    controllerAs: '$ctrl',
    bindToController: true,

    scope: {
      /**
       * @param {boolean} allowExcel
       * When `true`, allows Excel files extensions to be imported.
       */
      allowExcel: '<',
      /**
       * @param {expr} disableImport
       * Returns `true` if the import button should be disabled.
       */
      disableImport: '&',

      /**
       * @param {object} examples
       * If provided, will display a table of example data. This expects the
       * object to use the ExampleData class.
       */
      examples: '<',

      /**
       * @param {string} handler
       * Current url Handler url to set as the form action/service URL.
       */
      handler: '@',

      /**
       * @param {string} hideReset
       * Optional flag to hide the main reset button when using custom content.
       */
      hideReset: '@',

      /**
       * @param {expr=} inputValidation
       * Returns `true` if the form is valid and ready to be submitted.
       */
      inputValidation: '&?',

      /**
       * @param {boolean=} importStarted
       * Set `true` when import process starts.
       */
      importStarted: '=?',

      /**
       * @param {object} processMethods
       * Additional `window.scopeInterface` methods to be called from the IFRAME.
       *
       * Note: `scopeInterface` calls are from the IFRAME and outside of the
       *       application watch cycles. Use `$timeout` for scope changes so
       *       parent page views will be updated.
       */
      processMethods: '<',

      /**
       * @param {expr=} resetInput
       * Executed when Reset button is clicked.
       */
      resetInput: '&',

      /**
       * @param {expr=} showResultsOnError
       * Returns `true` to show results table when there are errors.
       */
      showResultsOnError: '&',

      /**
       * @param {string} templatePath
       * Optional template file location.
       */
      templatePath: '@',

      /**
       * @param {boolean} usingAjax
       * Use AJAX file upload instead of FORM submit. When `true`, import requires
       * two calls to the server:
       *   - `ngFileUpload` is used to POST the file to `handler`, showing an
       *     upload progress bar. `handler` saves the upload to a temporary
       *     file and responds with the file name.
       *   - a GET request to `handler` with the query variable `tempFileName`
       *     is set as the IFRAME `src` property, triggering the second call.
       */
      usingAjax: '<'
    },

    /*--------------------*/
    /* Transclusion Slots */
    /*--------------------*/
    transclude: {
      /**
       * @slot afterContent
       * Rendered after the file upload form.
       */
      after: '?afterContent',

      /**
       * @slot beforeContent
       * Rendered before the file upload form.
       */
      before: '?beforeContent',

      /**
       * @slot customContent
       * Form content to include before upload file input.
       */
      custom: '?customContent',

      /**
       * @slot errorsHead
       * Error table header replacement. Headers have to be wrapped in a table element to be valid markup.
       *   Example: <table><tr><th>Header1</th><th>Header2</th></tr></table>
       */
      errorsHead: '?errorsHead',

      /**
       * @slot resultsTable
       * Custom results table to show.
       */
      resultsTable: '?resultsTable'
    }
  };
}

/*******************************************************************************
 * importFormController */
/**
 * This controller keeps track of progress bar and summary total scopes while
 * providing for custom content using transclusion slots. Adds `scopeInterface`
 * to window object to handle updates to page from embedded IFRAME.
 *
 * This will emit the following events on the $rootScope:
 *   fileImportStart - Emitted when by the startImport call
 *   fileImportEnd   - Emitted when the import is finished successfully or when
 *                     an error has occured
 *******************************************************************************/
/* @ngInject */ function importFormController(
  $scope,
  $element,
  $injector,
  $transclude,
  CONST
) {
  $scope.CONST = CONST;
  var $svcs = {
    $ctrl: this,
    $element: $element,
    $scope: $scope,
    $transclude: $transclude
  };
  var $ctrl = Object.defineProperties(
    Object.assign(this, {
      /*----------*/
      /* Handlers */
      /*----------*/
      addBarScope: $injector.invoke(
        addBarScope,
        null,
        Object.assign($svcs, { $svcs })
      ),
      addResultRowScope: $injector.invoke(addResultRowScope, null, $svcs),
      disableSubmit: $injector.invoke(disableSubmit, null, $svcs),
      downloadTemplate: $injector.invoke(downloadTemplate, null, $svcs),
      getAcceptableExtensions: $injector.invoke(
        getAcceptableExtensions,
        null,
        $svcs
      ),
      getFileInput: $injector.invoke(getFileInput, null, $svcs),
      getHandler: $injector.invoke(getHandler, null, $svcs),
      hasCustom: $injector.invoke(hasCustom, null, $svcs),
      removeBarScope: $injector.invoke(removeBarScope, null, $svcs),
      removeResultRowScope: $injector.invoke(removeResultRowScope, null, $svcs),
      resetPage: $injector.invoke(resetPage, null, $svcs),
      showReset: $injector.invoke(showReset, null, $svcs),
      showTemplate: $injector.invoke(showTemplate, null, $svcs),
      submitForm: $injector.invoke(submitForm, null, $svcs),
      uploadFile: $injector.invoke(uploadFile, null, $svcs),
      $onInit: () => {
        /*-----------------------------------*/
        /* Scope interface called by IFrame. */
        /*-----------------------------------*/
        window.scopeInterface = $injector.invoke(
          windowScopeInterface,
          null,
          $svcs
        );
      },

      /*---------*/
      /* Content */
      /*---------*/
      bars: [], // Array of uploadBar scopes.
      bodyErrorsHtml: null, // Server validation errors content.
      iframeSrc: undefined, // IFRAME `src` for upload `using-ajax`.
      importErrorHtml: null, // Server import failure message.
      inputErrorHtml: null, // Server input file error message.
      results: [], // Array of resultRow scopes.

      /*-------*/
      /* Flags */
      /*-------*/
      hasErrors: false, // Errors in imported data flag.
      importStarted: false, // Import in progress flag.
      isUploading: false, // Upload in progress flag.
      needsFile: false, // File not selected flag.
      showFatal: false, // Show fatal content flag.
      showProgress: false, // Show progress bars flag.
      showResults: false // Show results content flag.
    }),
    {
      /*-----------*/
      /* Accessors */
      /*-----------*/
      fileInput: {
        get: function () {
          // Import file input field.
          return $element[0].querySelector('input[type=file]');
        }
      },

      importFile: {
        get: function () {
          // Selected import file.
          var files = ($ctrl.fileInput || {}).files || [];
          return files.length > 0 ? files[0] : null;
        }
      },

      importFileName: {
        get: function () {
          // Selected import file name.
          return ($ctrl.importFile || {}).name;
        }
      }
    }
  );

  /*------------------------------------------------*/
  /* Set errorsTableHead if they have been provided */
  /*------------------------------------------------*/
  if ($transclude.isSlotFilled('errorsHead')) {
    var thead = angular.element($element[0].querySelector('.errorsTableHead'));
    thead.empty();

    $transclude(
      function (content) {
        thead.append(content.find('tr'));
      },
      null,
      'errorsHead'
    );
  }
}

/*******************************************************************************
 * addBarScope */
/**
 * Called by `uploadBar` linker to add scope object to the bars array.
 *
 * Transcluded bars will come in before the upload bar so it will always be put
 * at the front of the list so the bar appears first.
 *
 * @param  barScope  Scope from an {@link uploadBar}
 *******************************************************************************/
/* @ngInject */ function addBarScope($ctrl, CONST) {
  return function (barScope) {
    if (barScope.id === CONST.UploadBarID) $ctrl.bars.unshift(barScope);
    else addToArray($ctrl.bars, barScope);
  };
}

/*******************************************************************************
 * addResultRowScope */
/**
 * Called by `resultRow` linker to add scope object to the results array
 *
 * @param  rowScope  Scope from a {@link resultRow}
 *******************************************************************************/
/* @ngInject */ function addResultRowScope($ctrl) {
  return function (rowScope) {
    addToArray($ctrl.results, rowScope);
  };
}

/*******************************************************************************
 * disableSubmit */
/**
 * Disable submit button flag. Returns `true` when upload or import is in
 * progress or `$ctrl.disableImport` evaluates to true.
 *******************************************************************************/
/* @ngInject */ function disableSubmit($ctrl) {
  return function () {
    return (
      $ctrl.isUploading || $ctrl.importStarted || $ctrl.disableImport() == true
    );
  };
}

/*******************************************************************************
 * downloadTemplate */
/**
 * Opens the template file in a new window for download.
 *******************************************************************************/
/* @ngInject */ function downloadTemplate($ctrl) {
  return function () {
    window.open($ctrl.templatePath);
  };
}

/*******************************************************************************
 * getAcceptableExtensions */
/**
 * Returns the allowable file extensions for import. `csv` is always allowed,
 * while `xls` and `xlsx` are only allowed if the importer being used works with
 * it (like ACEReader).
 *******************************************************************************/
/* @ngInject */ function getAcceptableExtensions($ctrl) {
  const extensions = [
    { name: '.csv', isCSV: true },
    { name: '.xls', isExcel: true },
    { name: '.xlsx', isExcel: true }
  ];
  return function () {
    return extensions
      .filter((ext) => ext.isCSV || ($ctrl.allowExcel && ext.isExcel))
      .map((ext) => ext.name)
      .join(',');
  };
}

/*******************************************************************************
 * getFileInput */
/**
 * Returns a reference to the file input element.
 *******************************************************************************/
/* @ngInject */ function getFileInput($element) {
  return function () {
    return $element[0].querySelector('input[type=file]');
  };
}

/*******************************************************************************
 * getHandler */
/**
 * Returns the handler to put on the form action attribute. The scope handler
 * value will be used if specified, otherwise, the current page will be returned.
 *******************************************************************************/
/* @ngInject */ function getHandler($ctrl) {
  return function () {
    return $ctrl.handler || location.href;
  };
}

/*******************************************************************************
 * hasCustom */
/**
 * Returns an indication the custom transclude slot has been used.
 *******************************************************************************/
/* @ngInject */ function hasCustom($transclude) {
  return function () {
    return $transclude.isSlotFilled('custom');
  };
}

/*******************************************************************************
 * removeBarScope */
/**
 * Removes the passed uploadBar scope object from the bars array
 *
 * @param  barScope  Scope from an {@link uploadBar}
 *******************************************************************************/
/* @ngInject */ function removeBarScope($ctrl) {
  return function (barScope) {
    removeFromArray($ctrl.bars, barScope);
  };
}

/*******************************************************************************
 * removeResultRowScope */
/**
 * Removes the passed resultRow scope object from the results array
 *
 * @param  rowScope  Scope from an {@link resultRow}
 *******************************************************************************/
/* @ngInject */ function removeResultRowScope($ctrl) {
  return function (rowScope) {
    removeFromArray($ctrl.results, rowScope);
  };
}

/*******************************************************************************
 * resetPage */
/**
 * Clears any set flags/values to return the directive to its initial form
 * import state.
 *******************************************************************************/
/* @ngInject */ function resetPage($ctrl, $element) {
  return function () {
    $element.find('form')[0].reset();

    $ctrl.bodyErrorsHtml = null;
    $ctrl.isUploading = false;
    $ctrl.hasErrors = false;
    $ctrl.importErrorHtml = null;
    $ctrl.inputErrorHtml = null;
    $ctrl.importStarted = false;
    $ctrl.needsFile = false;
    $ctrl.showFatal = false;
    $ctrl.showProgress = false;
    $ctrl.showResults = false;

    /*----------------------------------------*/
    /* Calls bound resetInput scope attribute */
    /*----------------------------------------*/
    $ctrl.resetInput();
  };
}

/*******************************************************************************
 * showReset */
/**
 * Show the main reset button when there is custom content and hideReset on
 * the scope has not been set to true.
 *******************************************************************************/
/* @ngInject */ function showReset($ctrl) {
  return function () {
    return $ctrl.hasCustom() && $ctrl.hideReset != 'true';
  };
}

/*******************************************************************************
 * showTemplate */
/**
 * Shows the Download Template button when there is a template path provided.
 *******************************************************************************/
/* @ngInject */ function showTemplate($ctrl, Tools) {
  return function () {
    return !Tools.isEmpty($ctrl.templatePath);
  };
}

/*******************************************************************************
 * submitForm */
/**
 * Form submission handler. Verifies a file has been selected and calls
 * `$ctrl.inputValidation` if supplied. if validation fails, form submit is
 * aborted.
 *
 * if `$ctrl.usingAjax` is set, form submission is suppressed, file is
 * uploaded to the service with `ngFileUpload` which returns the temp file
 * name, IFRAME `src` is set to service URL with `tempFileName` provided.
 *
 * @param  event  Submit event.
 *******************************************************************************/
/* @ngInject */ function submitForm($ctrl) {
  return function (event) {
    $ctrl.needsFile = !$ctrl.importFile;
    var validInput = $ctrl.inputValidation
      ? $ctrl.inputValidation({ $hasFile: !$ctrl.needsFile })
      : !$ctrl.needsFile;

    /*-----------------------------------------*/
    /* If using AJAX, suppress form submission */
    /* and upload file with `ngFileUpload`.    */
    /*-----------------------------------------*/
    if ($ctrl.usingAjax && validInput) {
      event.preventDefault();
      $ctrl.isUploading = true;
      $ctrl.uploadFile($ctrl.importFile).then(function (r) {
        /*----------------------------------------*/
        /* Setup IFRAME `src` to service URL and  */
        /* `tempFileName` to complete the import. */
        /*----------------------------------------*/
        $ctrl.iframeSrc = $ctrl.handler + '?tempFileName=' + encodeURI(r.data);
      });
    } else if (
      /*-----------------------------------------------------*/
      /* Prevent form submission if input validation failed. */
      /*-----------------------------------------------------*/
      !($ctrl.isUploading = validInput)
    )
      event.preventDefault();
  };
}

/*******************************************************************************
 * uploadFile */
/**
 * Uploads file to the server using `ngFileUpload`. Called by upload handler.
 * Updates upload progress bar as file uploads. Shows import or fatal error if
 * upload fails.
 *
 * @param  file  File object.
 * @param  errFiles  Files rejected by the file pattern validator.
 *******************************************************************************/
/* @ngInject */ function uploadFile($ctrl, $q, Upload, Alert) {
  var progressBarID = 'barUploadFile';

  return function (file, errFiles) {
    return $q(function (resolve, reject) {
      /*----------------------------------------------------*/
      /* If error, file did not pass the pattern validator. */
      /*----------------------------------------------------*/
      if (errFiles && errFiles[0])
        Alert.error(
          'Selected file is not a CSV file.',
          'Not CSV File',
          false
        ).then(reject);
      /*-----------------------------------------------------------*/
      /* Upload the new file to the server. Temporary image URL    */
      /* is returned in response and new image is shown in dialog. */
      /*-----------------------------------------------------------*/ else if (
        file
      ) {
        window.scopeInterface.startImport();
        window.scopeInterface.setBarVisible(progressBarID, true);
        window.scopeInterface.setBarValue(progressBarID, 0);

        Upload.upload({ url: $ctrl.handler, data: { file: file } }).then(
          /*------------------------------*/
          /* Success! Show the new image. */
          /*------------------------------*/
          function (r) {
            window.scopeInterface.setBarValue(progressBarID, 100);
            resolve(r);
          },

          /*-------------------------------------------------------*/
          /* Fail! For input error (418) status, show the error    */
          /* message and let the user retry. All others are fatal. */
          /*-------------------------------------------------------*/
          function (r) {
            if (r.status == 418)
              window.scopeInterface.importError(r.data.message);
            else window.scopeInterface.fatalError();
            reject();
          },

          /*------------------------------------*/
          /* Progress. Update the progress bar. */
          /*------------------------------------*/
          function (e) {
            window.scopeInterface.setBarValue(
              progressBarID,
              Math.min(100, parseInt((100.0 * e.loaded) / e.total))
            );
          }
        );
      }
    });
  };
}

/*******************************************************************************
 * windowScopeInterface */
/**
 * Service added to the parent window of the target IFRAME as a means of updating
 * the controller scope. Since these are called outside of the application watch
 * cycles, $timeout is used to fire a digest cycle for scope changes.
 *******************************************************************************/
/* @ngInject */ function windowScopeInterface($ctrl, $injector, $svcs) {
  return Object.assign(($svcs.$self = {}), $ctrl.processMethods || {}, {
    done: $injector.invoke(done, null, $svcs),
    fatalError: $injector.invoke(fatalError, null, $svcs),
    finishImport: $injector.invoke(finishImport, null, $svcs),
    getBarScope: $injector.invoke(getBarScope, null, $svcs),
    importError: $injector.invoke(importError, null, $svcs),
    inputError: $injector.invoke(inputError, null, $svcs),
    setBarMax: $injector.invoke(setBarMax, null, $svcs),
    setBarValue: $injector.invoke(setBarValue, null, $svcs),
    setBarVisible: $injector.invoke(setBarVisible, null, $svcs),
    showErrors: $injector.invoke(showErrors, null, $svcs),
    startImport: $injector.invoke(startImport, null, $svcs)
  });

  /*************************************************************************
   * window.scopeInterface.done */
  /**
   * Remove the progress bars and show the results section. The arguments
   * parameters are used to set the value property of the resultRow scopes
   * to display in the resulting table.
   *
   * @param {string} title  Results title text to show
   * @param {Array}  args   Additional arguments used to set values in the
   *                        corresponding resultRow scopes.
   *************************************************************************/
  /* @ngInject */
  function done($ctrl) {
    return function (title) {
      $ctrl.showProgress = false;
      $ctrl.showResults = true;
      $ctrl.resultsTitle = title;

      /*-----------------------------------------------*/
      /* Assign additional arguments to `$scope.value` */
      /* for corresponding <result-row> components.    */
      /*-----------------------------------------------*/
      if (arguments.length > 1 && $ctrl.results.length > 0)
        for (
          var i = 0;
          i < Math.min(arguments.length - 1, $ctrl.results.length);
          i++
        )
          $ctrl.results[i].value = arguments[i + 1];

      this.finishImport();
    };
  }

  /*************************************************************************
   * window.scopeInterface.fatalError */
  /**
   * Fatal server error has occurred, display the fatal error section.
   *************************************************************************/
  /* @ngInject */
  function fatalError($ctrl) {
    return function () {
      $ctrl.showFatal = true;
      this.finishImport();
    };
  }

  /***************************************************************************
   * window.scopeInterface.finishImport */
  /**
   * Send 'fileImportEnd' to ancestor elements.
   ***************************************************************************/
  /* @ngInject */
  function finishImport($ctrl, $rootScope, $timeout) {
    return function () {
      $timeout(function () {
        $rootScope.$emit('fileImportEnd');
      });
    };
  }

  /*************************************************************************
   * window.scopeInterface.getBarScope */
  /**
   * Returns the corresponding uploadBar scope object for the barID.
   *
   * @param  {string} barID  Progress bar element ID.
   * @return {scope}         Component scope.
   *************************************************************************/
  /* @ngInject */
  function getBarScope($ctrl) {
    return function (barID) {
      return $ctrl.bars
        .filter(function (bar) {
          return bar.id == barID;
        })
        .shift();
    };
  }

  /*************************************************************************
   * window.scopeInterface.importError */
  /**
   * Set the import error message which will display the content.
   *
   * @param {string} msg  Error message.
   *************************************************************************/
  /* @ngInject */
  function importError($ctrl, $sce) {
    return function (msg) {
      $ctrl.importErrorHtml = $sce.trustAsHtml(msg);
      this.finishImport();
    };
  }

  /*************************************************************************
   * window.scopeInterface.inputError */
  /**
   * Set the input error message. If there is a message, put the focus on the
   * file input.
   *
   * @param {string} msg  Error message.
   *************************************************************************/
  /* @ngInject */
  function inputError($ctrl, $sce) {
    return function (msg) {
      $ctrl.inputErrorHtml = $sce.trustAsHtml(msg);

      if (msg) {
        $ctrl.isUploading = false;
        $ctrl.fileInput.focus();
      }

      this.finishImport();
    };
  }

  /*************************************************************************
   * window.scopeInterface.setBarMax */
  /**
   * Set the max bar value for the barID.
   *
   * @param {string}  barID  Progress bar element ID.
   * @param {integer} max    Maximum value.
   *************************************************************************/
  /* @ngInject */
  function setBarMax($self, $timeout) {
    return function (barID, max) {
      $timeout(function () {
        $self.getBarScope(barID).max = max;
      });
    };
  }

  /*************************************************************************
   * window.scopeInterface.setBarValue */
  /**
   * Set the bar value for the barID.
   *
   * @param {string}  barID  Progress bar element ID.
   * @param {integer} value  Bar value.
   *************************************************************************/
  /* @ngInject */
  function setBarValue() {
    return function (barID, value) {
      var bs = this.getBarScope(barID);
      var current = bs.value;
      bs.value = value;

      /*------------------------------------*/
      /* Update only when the value changes */
      /*------------------------------------*/
      if (current != value) bs.update();
    };
  }

  /*************************************************************************
   * window.scopeInterface.setBarVisible */
  /**
   * Set progress bar visible flag.
   *
   * @param {string}  barID    Progress bar element ID.
   * @param {boolean} visible  Visible flag.
   *************************************************************************/
  /* @ngInject */
  function setBarVisible($self, $timeout) {
    return function (barID, visible) {
      $timeout(function () {
        $self.getBarScope(barID).visible = visible;
      });
    };
  }

  /*************************************************************************
   * window.scopeInterface.showErrors */
  /**
   * Validation failure, take error row html and set to bodyErrors to display.
   * The done method will trigger a digest call.
   *
   * @param errors  Html string of row errors
   *************************************************************************/
  /* @ngInject */
  function showErrors($ctrl, $sce) {
    return function (errors) {
      $ctrl.hasErrors = true;
      $ctrl.bodyErrorsHtml = $sce.trustAsHtml(errors);
      this.done('Validation Failed!');
    };
  }

  /*************************************************************************
   * window.scopeInterface.startImport */
  /**
   * The UI needs to change view to show progress and result information
   * as the file is being processed.
   *************************************************************************/
  /* @ngInject */
  function startImport($ctrl, $rootScope, $timeout) {
    return function () {
      $ctrl.inputErrorHtml = null;
      $ctrl.importStarted = true;
      $ctrl.showProgress = true;
      angular.forEach($ctrl.progressBars, function (bar) {
        bar.visible = false;
      });

      $timeout(function () {
        $rootScope.$emit('fileImportStart');
      });
    };
  }
}

/*******************************************************************************
 * addToArray */
/**
 * Adds an object to the array if its not in it.
 *
 * @param  array  Target array.
 * @param  obj  Object to add.
 *******************************************************************************/
function addToArray(array, obj) {
  if (array.indexOf(obj) == -1) array.push(obj);
}

/*******************************************************************************
 * removeFromArray */
/**
 * Removes the object from an array if its in it.
 *
 * @param  array  Target array.
 * @param  obj  Object to remove.
 *******************************************************************************/
function removeFromArray(array, obj) {
  var idx = array.indexOf(obj);
  if (idx > -1) array.splice(idx, 1);
}
